import { WSControlGroup } from "@wingspanhq/fe-component-library";
import {
  FundingSourceType,
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import {
  AccountUsage,
  Currency,
  IAccount
} from "@wingspanhq/users/dist/lib/interfaces";
import { WSQueryCache } from "@ws-react-query";
import { useMemo, useState } from "react";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import {
  QUERY_PAYOUT_SETTINGS,
  QUERY_PAYROLL_SETTINGS
} from "../../../query/payments/keys";
import {
  QUERY_USERS_ACCOUNT,
  QUERY_USERS_ACCOUNTS,
  QUERY_USERS_CLIENT
} from "../../../query/users/keys";
import { paymentsService } from "../../../services/payments";
import { usersService } from "../../../services/users";
import { StepLayoutWithOptions } from "../../../shared/components/Layout/StepLayoutWithOptions";
import { avatarBankAccount } from "../../../shared/constants/avatars";
import { selectorAccountInstitution } from "../../../shared/selectors/selectorAccountInstitution";
import { selectorAccountName } from "../../../shared/selectors/selectorAccountName";
import {
  concurrentActions,
  WSServiceError
} from "../../../utils/serviceHelper";

type Props = {
  title: string;
  description?: string;
  accounts: IAccount[];
  onBack?: () => void;
  onNext?: () => void;
  setFundingSource?: boolean;
  setPaymentMethod?: boolean;
  setStandardPayoutDestination?: boolean;
  setInstantPayoutDestination?: boolean;
};

async function updateAccountTypes(
  accounts: IAccount[],
  values: Record<string, AccountType>
) {
  return concurrentActions(
    accounts
      .filter(account => !account.usage)
      .map(account => async () => {
        try {
          const type = values[account.accountId];
          await usersService.account.update(account.accountId, {
            usage:
              type === "business"
                ? AccountUsage.Business
                : AccountUsage.Personal
          });
        } catch (error) {
          console.error("Failed to update account usage", error);
        }
      })
  );
}

type AccountType = "personal" | "business";

export const FormSelectAccount: React.FC<Props> = ({
  title,
  description,
  accounts,
  onBack,
  onNext,
  setFundingSource,
  setPaymentMethod,
  setStandardPayoutDestination,
  setInstantPayoutDestination
}) => {
  const userId = useUserId();

  const hasAccountsWithoutUsageSet = useMemo(
    () => accounts.some(account => !account.usage),
    [accounts]
  );

  const [accountTypeValues, setAccountTypeValues] = useState(
    accounts.reduce<Record<string, AccountType>>((acc, account) => {
      if (!!account.usage) {
        return acc;
      }

      return {
        ...acc,
        [account.accountId]: "personal"
      };
    }, {})
  );

  const [submit, meta] = useWSMutation<
    void,
    WSServiceError,
    { accountId: string }
  >(
    async ({ accountId }) => {
      // It's fine to run it in the background
      updateAccountTypes(accounts, accountTypeValues);

      if (setFundingSource) {
        const payrollSettings = await paymentsService.payrollSettings.update(
          userId,
          {
            fundingSource: {
              fundingSourceCurrency: Currency.USD,
              fundingSourceId: accountId,
              fundingSourceType: FundingSourceType.Account
            }
          }
        );

        WSQueryCache.setQueryData(QUERY_PAYROLL_SETTINGS, payrollSettings);
      }

      if (setPaymentMethod) {
        const client = await usersService.client.update(userId, {
          clientId: userId,
          profile: {
            defaultPaymentMethod: {
              accountId,
              paymentMethodId: null
            }
          }
        });

        WSQueryCache.setQueryData(QUERY_USERS_CLIENT, client);
      }

      if (setStandardPayoutDestination) {
        const payoutSettings = await paymentsService.payoutSettings.update(
          userId,
          {
            payoutPreferences: PayoutPreferences.Standard,
            standard: {
              destinationId: accountId,
              destinationType: PayoutDestinationType.Account
            }
          }
        );

        WSQueryCache.setQueryData(QUERY_PAYOUT_SETTINGS, payoutSettings);
      }

      if (setInstantPayoutDestination) {
        const payoutSettings = await paymentsService.payoutSettings.update(
          userId,
          {
            payoutPreferences: PayoutPreferences.Instant,
            instant: {
              destinationId: accountId,
              destinationType: PayoutDestinationType.Account
            }
          }
        );

        WSQueryCache.setQueryData(QUERY_PAYOUT_SETTINGS, payoutSettings);
      }
    },
    {
      onSuccess: onNext,
      dependencies: [
        QUERY_PAYROLL_SETTINGS,
        QUERY_PAYOUT_SETTINGS,
        QUERY_USERS_ACCOUNT,
        QUERY_USERS_ACCOUNTS
      ]
    }
  );

  return (
    <StepLayoutWithOptions
      key="accounts"
      title={title}
      description={description}
      options={accounts.map(account => ({
        header: {
          label: {
            text: selectorAccountInstitution(account),
            secondaryText: selectorAccountName(account),
            avatar: avatarBankAccount
          }
        },
        value: account.accountId,
        children: account.usage ? undefined : (
          <WSControlGroup
            label="Account type"
            helperText="Specify whether this is a personal or business bank account"
            type="radio"
            name={`account-type-${account.accountId}`}
            value={accountTypeValues[account.accountId]}
            onChange={newValue => {
              setAccountTypeValues(prev => ({
                ...prev,
                [account.accountId]: newValue as AccountType
              }));
            }}
            options={[
              {
                value: "personal",
                label: "Personal"
              },
              {
                value: "business",
                label: "Business"
              }
            ]}
          />
        )
      }))}
      onBack={onBack}
      onNext={accountId => {
        submit({ accountId });
      }}
      error={meta.error}
      isLoading={meta.isLoading}
      alerts={
        hasAccountsWithoutUsageSet
          ? [
              {
                theme: "info",
                icon: "info-circle",
                title: "Additional info required",
                children:
                  "Some accounts need additional information. Specify whether these are personal or business bank accounts."
              }
            ]
          : []
      }
    />
  );
};
